import React, { useState } from 'react';
import styled from 'styled-components';

import { AddressBar, Marquee, Mast, StatusBar, WEB, xpCss, webCss } from 'lib/xp';

import pfp from 'assets/pfp.png';

// VERIFY.exe — the anti-impostor window. On a young chain, fake tokens on the SAME
// pair are the day-one loss vector. This window states the rules of truth before
// anyone needs them. golive.ts flips LIVE + CA at launch (same constants as Buy).
//
// Restyled 2026-09-13 onto lib/xp's `web` layer (navy). The previous beige pass put
// `color: XP.ink` on `background: #000` in four places here — the contract address
// among them — which shipped genuinely invisible text. That is the whole reason the
// readability rule exists: dark ink on light, light ink on dark, never ink on ink.
// Navy because this is the authority page; it should read like a bank notice, not a
// shop. Constants below are grep-gated by golive.ts — do not rename or reformat them.

const LIVE = true;
const CA = '0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c';
const X_HANDLE = '@internetcoin888';

const AUTHORITY = [
  ['fee per trade', '2% — 1% creator tax + pons\' 1% base, fixed at creation, can never be raised'],
  ['split', '1.7% to holders, 0.3% to pons'],
  ['who can mint more', 'nobody — 1,000,000,000 fixed supply'],
  ['who can freeze / blacklist', 'nobody — the function does not exist'],
  ['liquidity', 'locked at graduation — no unlock function'],
  ['who receives the 1.7%', 'every holder, pro-rata — airdropped by pons\' distributor, no dev wallet in the loop'],
  ['dev bag at block zero', '0'],
];

const SOURCES = [
  ['this site', <>the file <code>/ca.txt</code> — plain text, no scripts, one line</>],
  [X_HANDLE, 'the pinned post, posted first, before anywhere else'],
  ['this window', 'the box below, with a copy button'],
];

export default function Verify() {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (!LIVE) return;
    try {
      navigator.clipboard.writeText(CA);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* ignore */
    }
  }
  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">Edit</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Authenticity Check</span>
      </div>
      <AddressBar path="verify.internetcoin.vip/ca.htm" />

      <div className="xp__client web web--navy">
        <Mast
          logo={pfp}
          kicker="Authenticity Check"
          title="VERIFY.exe"
          right={
            <div className="vf__shield">
              <span className="vf__shield__mark">✓</span>
              <span className="vf__shield__cap">VERIFIED</span>
            </div>
          }
        />
        <Marquee>
          ★ check the address character by character ★ same name, same logo, same pair — still fake ★
          the real one is below and nowhere else ★
        </Marquee>

        <div className="web__body">
          {/* The address itself comes first. It is the one thing anyone opens this window for. */}
          <div className="web__panel web__panel--white vf__ca">
            <div className="web__panel__t">The contract address</div>
            <div className="web__center">
              <span className="web__led web__led--big">{CA}</span>
            </div>
            <div className="web__center vf__ca__act">
              <button
                type="button"
                className={`web__btn${LIVE ? '' : ' web__btn--off'}`}
                onClick={copy}
                disabled={!LIVE}
              >
                {LIVE ? (copied ? 'COPIED ✓' : 'COPY ADDRESS') : 'NOT LIVE YET'}
              </button>
              <a className="web__btn web__btn--blue" href="/ca.txt" target="_blank" rel="noreferrer noopener">
                OPEN /ca.txt ↗
              </a>
            </div>
            <div className="web__note">
              {LIVE
                ? 'Any other address — same name, same logo, same pair — is fake. Check it against this box, character by character.'
                : 'There is no token yet. Any address claiming to be Internet Coin right now is fake. Especially the ones paired with MSFT. Especially the ones with this logo.'}
            </div>
          </div>

          <div className="web__center">
            <span className="web__sticker web__sticker--blink">DON'T GET RUGGED!</span>
          </div>

          <div className="web__panel web__panel--navy">
            <div className="web__panel__t">The only three places it will ever appear</div>
            {SOURCES.map(([who, what], i) => (
              <div className="web__step" key={who}>
                <span className="web__step__n">{i + 1}</span>
                <div>
                  <b>{who}</b>
                  {what}
                </div>
              </div>
            ))}
          </div>

          <div className="web__hr" />

          <div className="web__panel web__panel--navy">
            <div className="web__panel__t">Authority disclosure — read it before someone reads it to you</div>
            <table className="web__table">
              <tbody>
                {AUTHORITY.map(([k, v]) => (
                  <tr key={k}>
                    <th scope="row">{k}</th>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="web__foot">
            launchpad: pons V2 · holder fee sharing ON · chain 4663 · pair: tokenized MSFT
            <br />
            Best viewed in Internet Explorer 6 at 800×600 · © 2001 Internet Coin
          </div>
        </div>
      </div>

      <StatusBar>
        <span>Done</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  /* The masthead badge: a gold-rimmed seal, because a verification page should carry
     something that looks stamped rather than typed. */
  .vf__shield {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
  .vf__shield__mark {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff2a8, ${WEB.gold} 45%, ${WEB.goldDeep} 100%);
    color: ${WEB.goldInk};
    font: bold 20px/34px Tahoma, sans-serif;
    text-align: center;
    border: 2px solid #8a6d1c;
    box-shadow: 1px 2px 0 rgba(0, 0, 0, 0.4);
  }
  .vf__shield__cap {
    font: bold 9px Tahoma, sans-serif;
    letter-spacing: 2px;
    color: ${WEB.gold};
  }

  /* The address plate wraps, because a 42-character hex string in a narrow window that
     cannot wrap is the same bug as invisible text: the reader cannot see all of it. */
  .vf__ca .web__led--big {
    max-width: 100%;
    word-break: break-all;
    text-align: center;
  }
  .vf__ca__act {
    gap: 8px;
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    a.web__btn {
      text-decoration: none;
      display: inline-block;
    }
  }

  /* the inline <code> inside source #1 sits on cream, so it takes dark ink */
  .web__step code {
    background: #fff;
    border: 1px solid ${WEB.goldDeep};
    color: ${WEB.ink};
    padding: 0 5px;
    font-family: 'Courier New', monospace;
  }

  .web__table th {
    width: 38%;
    white-space: nowrap;
  }
  @media (max-width: 420px) {
    .web__table th {
      white-space: normal;
    }
  }
`;
